import api from '@/libs/axiosInstance';
import { devBoostEligibilityOrNull } from '@/libs/boostDevPreview';
import type { ApiResponse, MentionBoostEligibilityResponse } from '@fluenceur/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

const MENTION_BOOST_ELIGIBILITY_QUERY_KEY_PREFIX = 'mentionBoostEligibility';

export const mentionBoostEligibilityQueryKey = (mentionId: string | null | undefined) => [
  MENTION_BOOST_ELIGIBILITY_QUERY_KEY_PREFIX,
  mentionId,
];

const fetchMentionBoostEligibility = async (
  mentionId: string
): Promise<MentionBoostEligibilityResponse> => {
  const response = await api.get<ApiResponse<MentionBoostEligibilityResponse>>(
    `/dashboard/mentions/${mentionId}/boost-eligibility`
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Mention boost eligibility is unavailable.');
  }

  return response.data.data;
};

interface UseMentionBoostEligibilityQueryOptions {
  mentionId: string | null | undefined;
  enabled?: boolean;
}

export const useMentionBoostEligibilityQuery = ({
  mentionId,
  enabled = true,
}: UseMentionBoostEligibilityQueryOptions): UseQueryResult<
  MentionBoostEligibilityResponse,
  Error
> => {
  return useQuery<MentionBoostEligibilityResponse, Error>({
    queryKey: mentionBoostEligibilityQueryKey(mentionId),
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      const devPreview = devBoostEligibilityOrNull(mentionId);
      if (devPreview) {
        return Promise.resolve(devPreview);
      }
      return fetchMentionBoostEligibility(mentionId);
    },
    enabled: !!mentionId && enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export { fetchMentionBoostEligibility };
